import { scope, Traversal, type } from 'arktype';

import { addressSchema, swiftAddressSchema } from '@/schemas/address';
import { validIbanOrAccountNumber } from '@/schemas/utils';
import { BankAccountType } from '@/types/bankAccountType';
import { CopDocumentType } from '@/types/copDocumentType';
import { CountryType } from '@/types/country';
import { CurrencyCodeType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { PaymentMethodAccountType } from '@/types/paymentMethodAccountType';
import { bicCountryCode, isValidSwiftCode } from '@/utility/swift';

export const fiatAchPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.enumerated(CountryType.US).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.USD).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.ach),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: addressSchema,
  routingNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
}).readonly();

export const fiatWirePaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.enumerated(CountryType.US).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.USD).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.wire),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: addressSchema,
  routingNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
}).readonly();

export const fiatRtpPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.enumerated(CountryType.US).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.USD).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.rtp),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: addressSchema,
  routingNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
}).readonly();

export const fiatPixPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.pix),
  country: type.enumerated(CountryType.BR).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.BRL).as<CurrencyCodeType>(),
  pixKey: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  documentNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatSpeiPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.spei),
  country: type.enumerated(CountryType.MX).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.MXN).as<CurrencyCodeType>(),
  clabe: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  receiverName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCvuPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.cvu),
  country: type.enumerated(CountryType.AR).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.ARS).as<CurrencyCodeType>(),
  cvu: type('string')
    .narrow((cvu: string): cvu is string => {
      if (cvu.length !== 22) {
        return false;
      }
      return /^\d{22}$/.test(cvu);
    })
    .configure({ message: 'VALIDATION_CVU_INVALID' }),
  recipientName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

const fiatSwiftPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.valueOf(CountryType).as<CountryType>(),
  currency: type.valueOf(CurrencyCodeType).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.swift),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: swiftAddressSchema,
  swiftCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  iban: type('string > 0')
    .configure({ message: 'VALIDATION_REQUIRED' })
    .narrow(validIbanOrAccountNumber),
  isPobo: 'boolean',
  'registrationNumber?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'bankCode?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'mobileNumber?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
});

const requireZipCodeWhenNotHK = (
  data: typeof fiatSwiftPaymentMethodBaseSchema.infer,
  ctx: Traversal,
): boolean => {
  if (data.country !== CountryType.HK && data.address.zipCode.length === 0) {
    return ctx.reject({
      path: ['address', 'zipCode'],
      message: 'VALIDATION_REQUIRED',
    });
  }
  return true;
};

// The BIC's embedded country (positions 5-6) is the source of truth for where the funds
// land, so the selected destination country must agree with it. Only enforced once the BIC
// is a complete, well-formed code — partial entry is left to the format/lookup validation.
const swiftCountryMatchesBic = (
  data: typeof fiatSwiftPaymentMethodBaseSchema.infer,
  ctx: Traversal,
): boolean => {
  if (!isValidSwiftCode(data.swiftCode)) {
    return true;
  }
  const bicCountry = bicCountryCode(data.swiftCode);
  if (bicCountry !== null && bicCountry !== data.country) {
    return ctx.reject({
      path: ['swiftCode'],
      message: 'VALIDATION_SWIFT_BIC_COUNTRY_MISMATCH',
    });
  }
  return true;
};

export const fiatSwiftPaymentMethodSchema = fiatSwiftPaymentMethodBaseSchema
  .narrow(requireZipCodeWhenNotHK)
  .narrow(swiftCountryMatchesBic)
  .readonly();

// =============================================================================
// Thunes local corridors (caliza-core-api PR #3411)
//
// Each schema below mirrors the `credit_party_mappings` of one corridor in the
// backend's V20260427143522 Flyway migration. Currencies that share a field
// shape share a schema (e.g. INSTAPAY/PROMPTPAY/RTC all just need an account
// number, so they reuse `fiatGenericLocalPaymentMethodSchema`).
// =============================================================================

const fiatFasterPaymentsPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.fasterPayments),
  country: type.enumerated(CountryType.GB).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.GBP).as<CurrencyCodeType>(),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  iban: type('string > 0')
    .configure({ message: 'VALIDATION_REQUIRED' })
    .narrow(validIbanOrAccountNumber),
  address: addressSchema,
});
export const fiatFasterPaymentsPaymentMethodSchema =
  fiatFasterPaymentsPaymentMethodBaseSchema.readonly();

const fiatImpsPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.imps),
  country: type.enumerated(CountryType.IN).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.INR).as<CurrencyCodeType>(),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  // IFSC: 4 letters + 0 + 6 alphanumeric.
  ifscCode: type('string').narrow((value: string, ctx: Traversal): boolean => {
    if (value.length === 0) {
      return ctx.reject({ message: 'VALIDATION_REQUIRED' });
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
      return ctx.reject({ message: 'VALIDATION_IFSC_CODE_INVALID' });
    }
    return true;
  }),
  address: addressSchema,
});
export const fiatImpsPaymentMethodSchema = fiatImpsPaymentMethodBaseSchema.readonly();

const fiatEftPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.eft),
  country: type.enumerated(CountryType.CA).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.CAD).as<CurrencyCodeType>(),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  // Canadian routing: 5-digit transit + 3-digit institution = 8 digits, often
  // sent as 9 digits with a leading 0.
  routingCode: type('string').narrow((value: string, ctx: Traversal): boolean => {
    if (value.length === 0) {
      return ctx.reject({ message: 'VALIDATION_REQUIRED' });
    }
    if (!/^\d{8,9}$/.test(value)) {
      return ctx.reject({ message: 'VALIDATION_ROUTING_CODE_INVALID' });
    }
    return true;
  }),
  address: addressSchema,
});
export const fiatEftPaymentMethodSchema = fiatEftPaymentMethodBaseSchema.readonly();

const fiatTefPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.tef),
  country: type.enumerated(CountryType.CL).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.CLP).as<CurrencyCodeType>(),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  // Chile account types per Thunes Implementation Insights.
  accountType: type("'CHECKING' | 'SAVINGS' | 'VISTA' | 'RUT'").configure({
    message: 'VALIDATION_REQUIRED',
  }),
  address: addressSchema,
});
export const fiatTefPaymentMethodSchema = fiatTefPaymentMethodBaseSchema.readonly();

// IPP (AED) and FAST (TRY) are both IBAN-only on the backend.
const fiatIbanLocalPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.ipp, FiatAccountType.fast),
  country: type.enumerated(CountryType.AE, CountryType.TR).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.AED, CurrencyCodeType.TRY).as<CurrencyCodeType>(),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  iban: type('string > 0')
    .configure({ message: 'VALIDATION_REQUIRED' })
    .narrow(validIbanOrAccountNumber),
  address: addressSchema,
});
export const fiatIbanLocalPaymentMethodSchema = fiatIbanLocalPaymentMethodBaseSchema.readonly();

// Generic single-account-number local rails (PHP, ZAR, THB, IDR-BIFAST, GHS,
// KES-PESALINK, NGN, BOB, PEN). All collect bank name + account number + address.
const fiatGenericLocalPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(
    FiatAccountType.instapay,
    FiatAccountType.rtc,
    FiatAccountType.promptpay,
    FiatAccountType.biFast,
    FiatAccountType.ghipss,
    FiatAccountType.pesalink,
    FiatAccountType.nip,
    FiatAccountType.bobAch,
    FiatAccountType.cce,
  ),
  country: type
    .enumerated(
      CountryType.PH,
      CountryType.ZA,
      CountryType.TH,
      CountryType.ID,
      CountryType.GH,
      CountryType.KE,
      CountryType.NG,
      CountryType.BO,
      CountryType.PE,
    )
    .as<CountryType>(),
  currency: type
    .enumerated(
      CurrencyCodeType.PHP,
      CurrencyCodeType.ZAR,
      CurrencyCodeType.THB,
      CurrencyCodeType.IDR,
      CurrencyCodeType.GHS,
      CurrencyCodeType.KES,
      CurrencyCodeType.NGN,
      CurrencyCodeType.BOB,
      CurrencyCodeType.PEN,
    )
    .as<CurrencyCodeType>(),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: addressSchema,
});
export const fiatGenericLocalPaymentMethodSchema =
  fiatGenericLocalPaymentMethodBaseSchema.readonly();

// Mobile wallet (IDR + KES). Backend `credit_party_mappings` is just msisdn;
// provider is captured separately for display/lookup but optional on the wire.
export const fiatMobileWalletPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.mobileWallet),
  country: type.enumerated(CountryType.ID, CountryType.KE).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.IDR, CurrencyCodeType.KES).as<CurrencyCodeType>(),
  // E.164: leading + then 8–15 digits.
  msisdn: type('string').narrow((value: string, ctx: Traversal): boolean => {
    if (value.length === 0) {
      return ctx.reject({ message: 'VALIDATION_REQUIRED' });
    }
    if (!/^\+\d{8,15}$/.test(value)) {
      return ctx.reject({ message: 'VALIDATION_MSISDN_INVALID' });
    }
    return true;
  }),
  'provider?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

// CNAPS = China's domestic CNY clearing. China has no IBAN, so the account is a plain
// domestic account number; the CNAPS bank code (sent as bank_account.id) is the required
// routing field and the BIC is optional.
const fiatCnapsPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.enumerated(CountryType.CN).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.CNY).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.cnaps),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: swiftAddressSchema,
  // CNAPS codes are exactly 12 numeric digits. The bank picker is a strict dropdown that
  // writes a valid code on selection; this narrow guards against any other entry path.
  bankCode: type('string').narrow((value: string, ctx: Traversal): boolean => {
    if (value.length === 0) {
      return ctx.reject({ message: 'VALIDATION_REQUIRED' });
    }
    if (!/^\d{12}$/.test(value)) {
      return ctx.reject({ message: 'VALIDATION_CNAPS_CODE_INVALID' });
    }
    return true;
  }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  mobileNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  isPobo: 'boolean',
  'registrationNumber?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  // BIC is optional on CNAPS; the field is always rendered, so allow an empty string
  // (the recipient builder drops an empty BIC rather than sending it to the backend).
  'swiftCode?': 'string',
});

const requireCnapsZipCode = (
  data: typeof fiatCnapsPaymentMethodBaseSchema.infer,
  ctx: Traversal,
): boolean => {
  if (data.address.zipCode.length === 0) {
    return ctx.reject({ path: ['address', 'zipCode'], message: 'VALIDATION_REQUIRED' });
  }
  return true;
};

export const fiatCnapsPaymentMethodSchema = fiatCnapsPaymentMethodBaseSchema
  .narrow(requireCnapsZipCode)
  .readonly();

// CHATS = Hong Kong's HKD RTGS. Hong Kong has no IBAN, so the account is a plain domestic
// account number; the BIC routes the payout (LianLian also accepts a bank code, but the
// dashboard collects the BIC), just labelled as a domestic rail instead of "instructed SWIFT".
export const fiatChatsPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.enumerated(CountryType.HK).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.HKD).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.chats),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: swiftAddressSchema,
  swiftCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  mobileNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  isPobo: 'boolean',
  'registrationNumber?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'bankCode?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
}).readonly();

export const fiatSepaPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.valueOf(CountryType).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.EUR).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.sepa),
  iban: type('string > 0')
    .configure({ message: 'VALIDATION_REQUIRED' })
    .narrow(validIbanOrAccountNumber),
  'address?': 'undefined',
}).readonly();

export const fiatCOPBeneficiaryIndividualPaymentMethodSchema = type({
  firstName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  lastName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  type: type.enumerated(PaymentMethodAccountType.fiat),
  recipientType: type("'individual'").configure({ message: 'VALIDATION_REQUIRED' }),
  name: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  paymentMethod: type.enumerated(FiatAccountType.cop),
  country: type.enumerated(CountryType.CO).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.COP).as<CurrencyCodeType>(),
  bankAccount: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankType: type.valueOf(BankAccountType),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCOPBeneficiaryCompanyPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  recipientType: type("'company'").configure({ message: 'VALIDATION_REQUIRED' }),
  // name: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  paymentMethod: type.enumerated(FiatAccountType.cop),
  country: type.enumerated(CountryType.CO).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.COP).as<CurrencyCodeType>(),
  bankAccount: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankType: type.valueOf(BankAccountType),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  firstName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCOPThirdPartyIndividualPaymentMethodSchema = type({
  firstName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  lastName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  type: type.enumerated(PaymentMethodAccountType.fiat),
  recipientType: type("'individual'").configure({ message: 'VALIDATION_REQUIRED' }),
  // name: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  paymentMethod: type.enumerated(FiatAccountType.cop),
  country: type.enumerated(CountryType.CO).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.COP).as<CurrencyCodeType>(),
  bankAccount: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankType: type.valueOf(BankAccountType),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  thirdPartyWithdrawal: 'true',
  documentId: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  documentType: type.valueOf(CopDocumentType),
  email: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCOPThirdPartyCompanyPaymentMethodSchema = type({
  firstName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  type: type.enumerated(PaymentMethodAccountType.fiat),
  recipientType: type("'company'").configure({ message: 'VALIDATION_REQUIRED' }),
  // name: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  paymentMethod: type.enumerated(FiatAccountType.cop),
  country: type.enumerated(CountryType.CO).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.COP).as<CurrencyCodeType>(),
  bankAccount: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankType: type.valueOf(BankAccountType),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  thirdPartyWithdrawal: 'true',
  documentId: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  documentType: type.valueOf(CopDocumentType),
  email: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCopPaymentMethodSchema = scope({
  FiatCOPBeneficiaryIndividualPayment: fiatCOPBeneficiaryIndividualPaymentMethodSchema,
  FiatCOPBeneficiaryCompanyPayment: fiatCOPBeneficiaryCompanyPaymentMethodSchema,
  FiatCOPThirdPartyIndividualPayment: fiatCOPThirdPartyIndividualPaymentMethodSchema,
  FiatCOPThirdPartyCompanyPayment: fiatCOPThirdPartyCompanyPaymentMethodSchema,
})
  .type(
    'FiatCOPBeneficiaryIndividualPayment | FiatCOPBeneficiaryCompanyPayment | FiatCOPThirdPartyIndividualPayment | FiatCOPThirdPartyCompanyPayment',
  )
  .readonly();

export const paymentMethodFiatSchema = scope({
  FiatACHPayment: fiatAchPaymentMethodSchema,
  FiatWirePayment: fiatWirePaymentMethodSchema,
  FiatRTPPayment: fiatRtpPaymentMethodSchema,
  FiatPIXPayment: fiatPixPaymentMethodSchema,
  FiatSwiftPayment: fiatSwiftPaymentMethodSchema,
  FiatCnapsPayment: fiatCnapsPaymentMethodSchema,
  FiatChatsPayment: fiatChatsPaymentMethodSchema,
  FiatSepaPayment: fiatSepaPaymentMethodSchema,
  FiatCOPPayment: fiatCopPaymentMethodSchema,
  FiatSpeiPayment: fiatSpeiPaymentMethodSchema,
  FiatCvuPayment: fiatCvuPaymentMethodSchema,
  FiatFasterPaymentsPayment: fiatFasterPaymentsPaymentMethodSchema,
  FiatImpsPayment: fiatImpsPaymentMethodSchema,
  FiatEftPayment: fiatEftPaymentMethodSchema,
  FiatTefPayment: fiatTefPaymentMethodSchema,
  FiatIbanLocalPayment: fiatIbanLocalPaymentMethodSchema,
  FiatGenericLocalPayment: fiatGenericLocalPaymentMethodSchema,
  FiatMobileWalletPayment: fiatMobileWalletPaymentMethodSchema,
})
  .type(
    'FiatACHPayment | FiatWirePayment | FiatRTPPayment | FiatPIXPayment | FiatSwiftPayment | FiatCnapsPayment | FiatChatsPayment | FiatSepaPayment | FiatCOPPayment | FiatSpeiPayment | FiatCvuPayment | FiatFasterPaymentsPayment | FiatImpsPayment | FiatEftPayment | FiatTefPayment | FiatIbanLocalPayment | FiatGenericLocalPayment | FiatMobileWalletPayment',
  )
  .readonly();

export type FiatACHPaymentForm = typeof fiatAchPaymentMethodSchema.infer;
export type FiatWirePaymentForm = typeof fiatWirePaymentMethodSchema.infer;
export type FiatRtpPaymentForm = typeof fiatRtpPaymentMethodSchema.infer;
export type FiatPixPaymentForm = typeof fiatPixPaymentMethodSchema.infer;
export type FiatSwiftPaymentForm = typeof fiatSwiftPaymentMethodSchema.infer;
export type FiatCnapsPaymentForm = typeof fiatCnapsPaymentMethodSchema.infer;
export type FiatChatsPaymentForm = typeof fiatChatsPaymentMethodSchema.infer;
export type FiatSepaPaymentForm = typeof fiatSepaPaymentMethodSchema.infer;
export type FiatSpeiPaymentForm = typeof fiatSpeiPaymentMethodSchema.infer;
export type FiatFasterPaymentsPaymentForm = typeof fiatFasterPaymentsPaymentMethodSchema.infer;
export type FiatImpsPaymentForm = typeof fiatImpsPaymentMethodSchema.infer;
export type FiatEftPaymentForm = typeof fiatEftPaymentMethodSchema.infer;
export type FiatTefPaymentForm = typeof fiatTefPaymentMethodSchema.infer;
export type FiatIbanLocalPaymentForm = typeof fiatIbanLocalPaymentMethodSchema.infer;
export type FiatGenericLocalPaymentForm = typeof fiatGenericLocalPaymentMethodSchema.infer;
export type FiatMobileWalletPaymentForm = typeof fiatMobileWalletPaymentMethodSchema.infer;
export type FiatCOPBeneficiaryIndividualPaymentForm =
  typeof fiatCOPBeneficiaryIndividualPaymentMethodSchema.infer;
export type FiatCOPBeneficiaryCompanyPaymentForm =
  typeof fiatCOPBeneficiaryCompanyPaymentMethodSchema.infer;
export type FiatCOPThirdPartyIndividualPaymentForm =
  typeof fiatCOPThirdPartyIndividualPaymentMethodSchema.infer;
export type FiatCOPThirdPartyCompanyPaymentForm =
  typeof fiatCOPThirdPartyCompanyPaymentMethodSchema.infer;
export type FiatCOPPaymentForm = typeof fiatCopPaymentMethodSchema.infer;
export type FiatCvuPaymentForm = typeof fiatCvuPaymentMethodSchema.infer;

export type FiatPaymentForm = typeof paymentMethodFiatSchema.infer;

export type PaymentMethodForm = FiatPaymentForm;

export const isFiatACHPaymentForm = (value: PaymentMethodForm): value is FiatACHPaymentForm =>
  fiatAchPaymentMethodSchema.allows(value);

export const isFiatWirePaymentForm = (value: PaymentMethodForm): value is FiatWirePaymentForm =>
  fiatWirePaymentMethodSchema.allows(value);

export const isFiatRtpPaymentForm = (value: PaymentMethodForm): value is FiatRtpPaymentForm =>
  fiatRtpPaymentMethodSchema.allows(value);

export const isFiatPixPaymentForm = (value: PaymentMethodForm): value is FiatPixPaymentForm =>
  fiatPixPaymentMethodSchema.allows(value);

export const isFiatSwiftPaymentForm = (value: PaymentMethodForm): value is FiatSwiftPaymentForm =>
  fiatSwiftPaymentMethodSchema.allows(value);

export const isFiatCnapsPaymentForm = (value: PaymentMethodForm): value is FiatCnapsPaymentForm =>
  fiatCnapsPaymentMethodSchema.allows(value);

export const isFiatChatsPaymentForm = (value: PaymentMethodForm): value is FiatChatsPaymentForm =>
  fiatChatsPaymentMethodSchema.allows(value);

export const isFiatSepaPaymentForm = (value: PaymentMethodForm): value is FiatSepaPaymentForm =>
  fiatSepaPaymentMethodSchema.allows(value);

export const isFiatSpeiPaymentForm = (value: PaymentMethodForm): value is FiatSpeiPaymentForm =>
  fiatSpeiPaymentMethodSchema.allows(value);

export const isFiatCOPBeneficiaryPaymentForm = (
  value: PaymentMethodForm,
): value is FiatCOPBeneficiaryIndividualPaymentForm | FiatCOPBeneficiaryCompanyPaymentForm =>
  fiatCOPBeneficiaryIndividualPaymentMethodSchema.allows(value) ||
  fiatCOPBeneficiaryCompanyPaymentMethodSchema.allows(value);

export const isFiatCOPThirdPartyPaymentForm = (
  value: PaymentMethodForm,
): value is FiatCOPThirdPartyIndividualPaymentForm | FiatCOPThirdPartyCompanyPaymentForm =>
  fiatCOPThirdPartyIndividualPaymentMethodSchema.allows(value) ||
  fiatCOPThirdPartyCompanyPaymentMethodSchema.allows(value);

export const isFiatCOPPaymentForm = (value: PaymentMethodForm): value is FiatCOPPaymentForm =>
  fiatCopPaymentMethodSchema.allows(value);

export const isFiatCvuPaymentForm = (value: PaymentMethodForm): value is FiatCvuPaymentForm =>
  fiatCvuPaymentMethodSchema.allows(value);

export const isFiatFasterPaymentsPaymentForm = (
  value: PaymentMethodForm,
): value is FiatFasterPaymentsPaymentForm => fiatFasterPaymentsPaymentMethodSchema.allows(value);

export const isFiatImpsPaymentForm = (value: PaymentMethodForm): value is FiatImpsPaymentForm =>
  fiatImpsPaymentMethodSchema.allows(value);

export const isFiatEftPaymentForm = (value: PaymentMethodForm): value is FiatEftPaymentForm =>
  fiatEftPaymentMethodSchema.allows(value);

export const isFiatTefPaymentForm = (value: PaymentMethodForm): value is FiatTefPaymentForm =>
  fiatTefPaymentMethodSchema.allows(value);

export const isFiatIbanLocalPaymentForm = (
  value: PaymentMethodForm,
): value is FiatIbanLocalPaymentForm => fiatIbanLocalPaymentMethodSchema.allows(value);

export const isFiatGenericLocalPaymentForm = (
  value: PaymentMethodForm,
): value is FiatGenericLocalPaymentForm => fiatGenericLocalPaymentMethodSchema.allows(value);

export const isFiatMobileWalletPaymentForm = (
  value: PaymentMethodForm,
): value is FiatMobileWalletPaymentForm => fiatMobileWalletPaymentMethodSchema.allows(value);
